import { useState, useEffect, useContext } from "react";
import { User, Users, MessageSquare, Mic, Circle, Clock, MinusCircle, EyeOff, Video, Ban, VolumeX, UserPlus, Heart, ToggleLeft, ToggleRight, Flag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import UserActionMenu from "./UserActionMenu";
import Avatar from "./Avatar";
import Nameplate from "./Nameplate";

// Status icon component
const StatusIcon = ({ status, statusMessage, size = "w-2 h-2" }) => {
  // If offline, show gray dot
  if (status !== "online") {
    return <Circle className={`${size} text-gray-400 fill-gray-400`} />;
  }
  
  // Show status based on status_message
  switch (statusMessage) {
    case "Away":
      return <Clock className={`${size} text-yellow-500`} />;
    case "Busy":
      return <MinusCircle className={`${size} text-red-500`} />;
    case "Invisible":
      return <EyeOff className={`${size} text-gray-500`} />;
    case "Available":
    default:
      return <Circle className={`${size} text-green-500 fill-green-500`} />;
  }
};

const UserListPanel = ({ 
  users, 
  onUserClick, 
  currentUserId,
  roomId,
  roomName, 
  voiceUsers = [],
  onRequestWebcam,
  onInviteToCam,
  onViewProfile,
  webcamViewerCounts = {},  // Map of user_id -> viewer count
  ignoredIds = [],
  onIgnoreChanged,
  onReport
}) => {
  const { token, user: currentUser } = useContext(AuthContext);
  // Mic-muted badge is only visible to admins/mods so they can spot voice-banned users quickly.
  const canSeeMicBadge = !!(currentUser?.is_admin || currentUser?.is_moderator);
  const [menuUser, setMenuUser] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState("room"); // "room" or "friends"
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [showOnlineOnly, setShowOnlineOnly] = useState(false); // Toggle for online friends only
  
  const isInVoice = (userId) => voiceUsers.some(v => v.user_id === userId);

  // Fetch friends list
  useEffect(() => {
    if (activeTab === "friends" && token) {
      fetchFriends();
    }
  }, [activeTab, token]);

  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      const res = await axios.get(`${API}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The /friends endpoint returns an array directly, not { friends: [] }
      setFriends(Array.isArray(res.data) ? res.data : res.data.friends || []);
    } catch (error) {
      console.error("Failed to fetch friends:", error);
    }
    setLoadingFriends(false);
  };

  // Invite friend to room
  const inviteFriend = async (friend) => {
    try {
      await axios.post(`${API}/room-invite`, {
        target_user_id: friend.user_id,
        room_id: roomId,
        room_name: roomName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite sent to ${friend.username}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    }
  };

  const handleUserClick = (e, chatUser) => {
    if (e && e.preventDefault) e.preventDefault();
    if (chatUser.user_id === currentUserId) return;
    
    // Get click position for menu
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      x: Math.min(rect.right + 5, window.innerWidth - 200),
      y: Math.min(rect.top, window.innerHeight - 260)
    });
    setMenuUser(chatUser);
  };

  const handleStartPM = (targetUser) => {
    onUserClick(targetUser);
    setMenuUser(null);
  };

  return (
    <div className="retro-window h-full flex flex-col" data-testid="user-list-panel">
      {/* Tab Switcher */}
      <div className="flex border-b border-[#808080]">
        <button
          className={`flex-1 py-1.5 text-xs font-bold flex items-center justify-center gap-1 ${
            activeTab === "room" 
              ? "bg-[#C0C0C0] border-t-2 border-l-2 border-white" 
              : "bg-[#808080] text-white"
          }`}
          onClick={() => setActiveTab("room")}
        >
          <Users className="w-3 h-3" />
          Room ({users.length})
        </button>
        <button
          className={`flex-1 py-1.5 text-xs font-bold flex items-center justify-center gap-1 ${
            activeTab === "friends" 
              ? "bg-[#C0C0C0] border-t-2 border-r-2 border-white" 
              : "bg-[#808080] text-white"
          }`}
          onClick={() => setActiveTab("friends")}
        >
          <Heart className="w-3 h-3" />
          Friends ({friends.length})
        </button>
      </div>
      
      <div className="flex-1 retro-inset overflow-y-auto retro-scrollbar">
        {/* Room Users Tab */}
        {activeTab === "room" && (
          <>
            {users.length === 0 ? (
              <div className="p-4 text-center">
                <p className="font-arial text-xs text-[#808080]">No users in room</p>
              </div>
            ) : (
              <div className="p-1">
                {[...users].sort((a, b) => {
                  const aOn = a.webcam_on && !ignoredIds.includes(a.user_id);
                  const bOn = b.webcam_on && !ignoredIds.includes(b.user_id);
                  if (aOn !== bOn) return aOn ? -1 : 1;           // broadcasters first
                  if (aOn && bOn) {                                // then by viewer count desc
                    return (webcamViewerCounts[b.user_id] || 0) - (webcamViewerCounts[a.user_id] || 0);
                  }
                  return 0;
                }).map((chatUser) => (
                  <div
                    key={chatUser.user_id}
                    className={`user-list-item ${chatUser.user_id === currentUserId ? 'opacity-60' : 'cursor-pointer'}`}
                    onContextMenu={(e) => handleUserClick(e, chatUser)}
                    onDoubleClick={() => chatUser.user_id !== currentUserId && onUserClick?.(chatUser)}
                    title={chatUser.user_id !== currentUserId ? "Double-click to message · right-click for options" : ""}
                    data-testid={`user-${chatUser.user_id}`}
                  >
                    <Avatar
                      avatar={chatUser.avatar}
                      username={chatUser.username}
                      color={chatUser.avatar_color || "#7B0099"}
                      size={20}
                      className="flex-shrink-0"
                    />
                    <StatusIcon 
                      status={chatUser.status} 
                      statusMessage={chatUser.status_message || "Available"} 
                      size="w-2 h-2"
                    />
                    <span className="font-arial text-xs truncate flex-1">
                      {chatUser.username}
                      {chatUser.user_id === currentUserId && " (You)"}
                      <Nameplate nameplate={chatUser.avatar?.nameplate} />
                      {chatUser.user_role === "vip" && (
                        <span className="ml-1 text-yellow-500" title="VIP Member">👑</span>
                      )}
                      {chatUser.is_admin && (
                        <span className="ml-1 text-red-500" title="Administrator">⚡</span>
                      )}
                      {chatUser.is_moderator && !chatUser.is_admin && (
                        <span className="ml-1 text-blue-500" title="Moderator">🛡️</span>
                      )}
                      {canSeeMicBadge && chatUser.voice_banned && (
                        <span
                          className="ml-1 inline-flex items-center gap-0.5 px-1 py-0 rounded-sm bg-red-100 text-red-700 text-[9px] font-bold align-middle"
                          title="Banned from voice chat (admin/mod only view)"
                          data-testid={`mic-muted-badge-${chatUser.user_id}`}
                        >
                          🎙️🚫 MUTED
                        </span>
                      )}
                    </span>
                    {/* LIVE badge with viewer count (hidden for ignored users) */}
                    {chatUser.webcam_on && !ignoredIds.includes(chatUser.user_id) && (
                      <span
                        className="flex items-center gap-1 px-1 rounded-sm bg-red-600 text-white text-[9px] font-bold flex-shrink-0"
                        title={`LIVE - ${webcamViewerCounts[chatUser.user_id] || 0} viewers`}
                        data-testid={`live-badge-${chatUser.user_id}`}
                      >
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE {webcamViewerCounts[chatUser.user_id] || 0}
                      </span>
                    )}
                    {isInVoice(chatUser.user_id) && (
                      <div className="voice-indicator">
                        <Mic className="w-3 h-3 text-green-500" />
                      </div>
                    )}
                    {chatUser.user_id !== currentUserId && (
                      <MessageSquare className="w-3 h-3 opacity-50" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Friends Tab */}
        {activeTab === "friends" && (
          <>
            {/* Online/All Toggle */}
            <div className="px-2 py-1 bg-[#E0E0E0] border-b border-[#808080] flex items-center justify-between">
              <span className="text-[10px] font-bold text-gray-600">
                {showOnlineOnly ? "Online Friends" : "All Friends"}
              </span>
              <button
                onClick={() => setShowOnlineOnly(!showOnlineOnly)}
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded hover:bg-gray-300"
                data-testid="online-toggle-btn"
              >
                {showOnlineOnly ? (
                  <><ToggleRight className="w-4 h-4 text-green-500" /> Online</>
                ) : (
                  <><ToggleLeft className="w-4 h-4 text-gray-400" /> All</>
                )}
              </button>
            </div>
            {loadingFriends ? (
              <div className="p-4 text-center">
                <p className="font-arial text-xs text-[#808080]">Loading friends...</p>
              </div>
            ) : friends.length === 0 ? (
              <div className="p-4 text-center">
                <p className="font-arial text-xs text-[#808080]">No friends yet</p>
              </div>
            ) : (
              <div className="p-1">
                {friends
                  .filter(friend => !showOnlineOnly || friend.status === "online")
                  .map((friend) => (
                  <div
                    key={friend.user_id}
                    className="user-list-item cursor-pointer"
                    onContextMenu={(e) => handleUserClick(e, friend)}
                    onDoubleClick={() => onUserClick?.(friend)}
                    title="Double-click to message · right-click for options"
                  >
                    <div 
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: friend.avatar_color || "#7B0099" }}
                    />
                    <StatusIcon 
                      status={friend.status} 
                      statusMessage={friend.status_message || "Available"} 
                      size="w-2 h-2"
                    />
                    <span className="font-arial text-xs truncate flex-1">
                      {friend.username}
                      {friend.status !== "online" && (
                        <span className="text-gray-500 text-[10px] ml-1">(offline)</span>
                      )}
                    </span>
                    {/* Invite button */}
                    <button
                      className="px-1.5 py-0.5 bg-green-600 hover:bg-green-500 text-white text-[10px] rounded font-bold"
                      onClick={(e) => {
                        e.stopPropagation();
                        inviteFriend(friend);
                      }}
                      title="Invite to this room"
                    >
                      <UserPlus className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-1 bg-[#C0C0C0] border-t border-[#808080]">
        <p className="font-arial text-xs text-center text-[#808080]">
          {activeTab === "room" ? "Click name for options" : "Click invite to bring friend here"}
        </p>
      </div>

      {/* User Action Menu - Centered Modal */}
      {menuUser && (
        <div 
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-[9999]"
          onClick={() => setMenuUser(null)}
        >
          <div 
            className="retro-window min-w-[200px] shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="retro-title-bar">
              <span className="text-xs">{menuUser.username}</span>
              <button className="close-button-visible" onClick={() => setMenuUser(null)}>✕</button>
            </div>
            <div className="p-1 bg-[#C0C0C0]">
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={() => { handleStartPM(menuUser); setMenuUser(null); }}
              
                data-testid="userlist-menu-pm"
              >
                <MessageSquare className="w-3 h-3 text-blue-600" /> Send Message
              </button>
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={() => { onRequestWebcam?.(menuUser); setMenuUser(null); }}
              
                data-testid="userlist-menu-webcam"
              >
                <Video className="w-3 h-3 text-purple-600" /> Request Webcam
              </button>
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={() => { onInviteToCam?.(menuUser); setMenuUser(null); }}
                data-testid="userlist-menu-invite-cam"
              >
                <Video className="w-3 h-3 text-pink-600" /> Invite to View Cam
              </button>
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={() => { onViewProfile?.(menuUser); setMenuUser(null); }}
              
                data-testid="userlist-menu-profile"
              >
                <User className="w-3 h-3 text-green-600" /> View Profile
              </button>
              <div className="border-t border-[#808080] my-1" />
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/users/block/${menuUser.user_id}`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success(`Blocked ${menuUser.username}`);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to block user");
                  }
                  setMenuUser(null);
                }}
              
                data-testid="userlist-menu-block"
              >
                <Ban className="w-3 h-3 text-red-600" /> Block User
              </button>
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={async () => {
                  const nowIgnored = !ignoredIds.includes(menuUser.user_id);
                  try {
                    if (nowIgnored) {
                      await axios.post(`${API}/users/ignore/${menuUser.user_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(`${menuUser.username} is now ignored`);
                    } else {
                      await axios.post(`${API}/users/unignore/${menuUser.user_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success(`${menuUser.username} is no longer ignored`);
                    }
                    onIgnoreChanged?.(menuUser, nowIgnored);
                  } catch (error) {
                    const d = error.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : "Failed to update ignore");
                  }
                  setMenuUser(null);
                }}
              
                data-testid="userlist-menu-ignore"
              >
                {ignoredIds.includes(menuUser.user_id)
                  ? (<><EyeOff className="w-3 h-3 text-green-600" /> Stop Ignoring</>)
                  : (<><VolumeX className="w-3 h-3 text-orange-600" /> Ignore User</>)}
              </button>
              <button 
                className="w-full text-left px-2 py-1.5 hover:bg-[#000080] hover:text-white text-xs flex items-center gap-2"
                onClick={() => { onReport?.(menuUser); setMenuUser(null); }}
              
                data-testid="userlist-menu-report"
              >
                <Flag className="w-3 h-3 text-red-700" /> Report User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserListPanel;
